import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { applyFilters, parseFilters } from "@/lib/filters";

import {
  MARKETPLACES,
  getBenchmarkData,
  isPending,
  messageFor,
  type Marketplace,
  type Metric,
} from "./api";
import { BenchmarkChart } from "./components/BenchmarkChart";
import {
  badgeVariant,
  defaultMarketplace,
  defaultVariant,
  freshnessFlag,
  marketplaceLabel,
  metricLabel,
  variantOptions,
  worstFreshness,
} from "./format";

/**
 * The Pricing and BSR tabs. One component, because they differ only in which
 * metric block they read — two near-identical files would drift the first time
 * one of them got a fix.
 */
export function ExplorerTab({ metric, areaName }: { metric: Metric; areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);

  // Selection lives in the URL so "look at DE pricing for the C8 variant" is a
  // link, not a paragraph of instructions.
  const marketplace: Marketplace = defaultMarketplace(
    typeof values.marketplace === "string" ? values.marketplace : null,
  );
  const requestedVariant = typeof values.variant === "string" ? values.variant : null;

  const query = useQuery({
    queryKey: ["meteoric_competitor_benchmark", "data", marketplace],
    queryFn: () => getBenchmarkData(marketplace),
  });

  const today = useMemo(() => new Date(), []);

  const payload = query.data && !isPending(query.data) ? query.data : null;
  const options = payload ? variantOptions(payload) : [];
  const activeVariant = payload ? defaultVariant(payload, metric, requestedVariant) : null;

  const filters: FilterSpec[] = [
    {
      name: "marketplace",
      label: "Marketplace",
      kind: "choice",
      options: MARKETPLACES.map((m) => ({ value: m, label: marketplaceLabel(m) })),
    },
    {
      name: "variant",
      label: "Product variant",
      kind: "choice",
      options: options.map((o) => ({ value: o.id, label: o.label })),
    },
  ];

  const onChange = (next: Record<string, string | string[] | null>) => {
    void navigate({ to: ".", search: applyFilters(search, next), replace: true });
  };

  const entry = payload && activeVariant ? payload.variants[activeVariant] : null;
  const block = entry ? entry[metric] : null;
  const banner = payload ? worstFreshness(payload, metric, today) : null;
  const variantFlag = block ? freshnessFlag(block.freshness_status, block.last_collected_at, today) : null;
  const latest = block && block.series.length > 0 ? block.series[block.series.length - 1] : null;

  return (
    <div className="flex flex-col gap-6">
      <FilterBar filters={filters} values={values} onChange={onChange} />

      {query.isPending && <Skeleton className="h-80 w-full" />}

      {query.isError && (
        <Alert>
          <AlertTitle>{metricLabel(metric)} data unavailable</AlertTitle>
          <AlertDescription>{messageFor(query.error, areaName)}</AlertDescription>
        </Alert>
      )}

      {/* Pre-first-run is its own state, not an error and not an empty chart.
          "Nothing collected yet" and "collection is broken" call for opposite
          responses, and only one of them needs anybody's attention today. */}
      {query.data && isPending(query.data) && (
        <Alert>
          <AlertTitle>Awaiting the first collection</AlertTitle>
          <AlertDescription>
            Nothing has been collected for {marketplaceLabel(marketplace)} yet. Add competitors on the
            Competitive Set tab, then trigger a collection there.
          </AlertDescription>
        </Alert>
      )}

      {payload && banner && banner.tone !== "ok" && (
        <Alert>
          <AlertTitle>Data recency</AlertTitle>
          <AlertDescription>
            {banner.message}
            {/* The banner reports the WORST variant, and says so: otherwise a
                reader takes it as a statement about the one they are looking at. */}
            <span className="block text-xs text-muted-foreground">
              This reports the most degraded variant in {marketplaceLabel(marketplace)}, not the one
              selected below.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {payload && options.length === 0 && (
        <Alert>
          <AlertTitle>No variants in this marketplace</AlertTitle>
          <AlertDescription>
            Nothing is being benchmarked in {marketplaceLabel(marketplace)}. Add a competitor on the
            Competitive Set tab to start.
          </AlertDescription>
        </Alert>
      )}

      {entry && block && (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <BenchmarkChart series={block.series} metric={metric} marketplace={marketplace} />

            <div className="flex flex-wrap items-center gap-4 border-t border-border pt-3 text-xs text-muted-foreground">
              <span>
                Last collected{" "}
                <strong className="text-ks-ink">{block.last_collected_at ?? "never"}</strong>
              </span>
              {latest && (
                <span>
                  Latest day built from <strong className="text-ks-ink">{latest.n}</strong>{" "}
                  competitors
                </span>
              )}
              {variantFlag && variantFlag.tone !== "ok" && (
                <Badge variant={badgeVariant(variantFlag.tone)}>{variantFlag.message}</Badge>
              )}
              {payload && (
                <span className="ml-auto">
                  Computed {payload.computed_at.slice(0, 16).replace("T", " ")} UTC
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
